import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommonMonthlyBar,
  CommonSeparator,
  CommonMonthlyContainer,
  CommonColumnStyle,
  buttonStyle,
  disableViewButtonStyle,
  buttonStyleForecast,
  Month,
  Item,
  Label,
  Value,
  PillContainer,
  MonthlyBarStyle,
  mobileBtnStyle,
  MonthBtnStyle,
  buttonStyleForecastPL,
  buttonStyleForecastGCSP,
  buttonStyleForecastMSL,
  MissedText,
  ItemMSLFuture,
  LabelMSLFuture,
  ValueMSLFuture,
} from '@atoms/dashboard/quarterlyDetails.styles';
import { InvisibleDiv2 } from '@atoms/sidebar/sidebar.styles';
import { MotifButton } from '@ey-xd/motif-react';
import { CurrentPill, FuturePillDashboard } from '@constants/pursuitLeaderConstants';
import { useRecoilValue } from 'recoil';
import {
  Recoil_Quarter_State,
  System_Date_Time_State,
  Recoil_Selected_Account_State,
} from '@recoil/recoil';
import {
  formatNumberWithKAndM,
  getAccuracy,
  uppercaseFirstLetter,
  GcspformatNumberWithKAndM,
  formatVarianceWithKAndM,
} from '@util/commonUtil';
import { isMobile } from 'react-device-detect';
import {
  BUTTON_TITLE,
  QUARTERLY_DETAILS,
  MONTH_QUARTER_STATUS,
  ROLE_CONST,
} from '@constants/commonConstants';

const MonthlyDetails = props => {
  const { quarterData, email, account, auditRiskData } = props;
  const navigate = useNavigate();
  const monthDataInfo = quarterData.data['months'];
  const quarterDataInfo = quarterData.data['quarterlyCalculation'];
  const quarterInfo = useRecoilValue(Recoil_Quarter_State);
  const systemDateTime = useRecoilValue(System_Date_Time_State);
  const selectedUser = useRecoilValue(Recoil_Selected_Account_State);
  const TodaysDate = new Date(systemDateTime);
  const currentRole = selectedUser?.role;

  const getValue = (tag, value) => {
    if (tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE) {
      return QUARTERLY_DETAILS.NOT_APPLICABLE;
    } else {
      return formatNumberWithKAndM(value) || '0 K';
    }
  };

  const totalForecastValue = (data, currentMonth) => {
    let plCommitValue;

    // Apply period-based logic for GCSP: pl_commit for current/future, commit for past
    // Prioritize actual_period over period for determining which value to use
    if (currentRole === ROLE_CONST?.GCSP) {
      if (
        data?.actual_period === 'current' ||
        data?.actual_period === 'future' ||
        data?.period === 'future'
      ) {
        plCommitValue = data?.pl_commit ?? 0;
      } else {
        plCommitValue = data?.commit ?? 0;
      }
    } else {
      plCommitValue = data?.pl_commit ?? 0;
    }

    // Add audit risk for MSL and GCSP roles for all periods (current, future, and past) when month matches
    if (
      (currentRole === ROLE_CONST?.MSL || currentRole === ROLE_CONST?.GCSP) &&
      currentMonth &&
      (data?.actual_period === 'current' || data?.period === 'future' || data?.period === 'past')
    ) {
      if (auditRiskData && Array.isArray(auditRiskData)) {
        const matchingAuditRisk = auditRiskData.find(
          auditItem => auditItem?.month === currentMonth,
        );
        if (matchingAuditRisk) {
          // Ensure both values are numbers to avoid string concatenation
          const basePlCommit = Number(plCommitValue) || 0;
          const auditRisk = Number(matchingAuditRisk?.value) || 0;
          plCommitValue = basePlCommit + auditRisk;
        }
      }
    }

    const totalForecastVal = plCommitValue + (data?.gcsp_judge ?? 0) + (data?.judge ?? 0);
    return formatNumberWithKAndM(totalForecastVal) || '0 K';
  };

  const forecastLabel = data => {
    if (data?.actual_period === 'current') {
      if (currentRole === 'PL') {
        return QUARTERLY_DETAILS.FORECAST;
      } else if (currentRole === 'MSL') {
        return QUARTERLY_DETAILS.PL_FORECAST_MSL;
      } else {
        return QUARTERLY_DETAILS.PL_FORECAST_GCSP;
      }
    } else if (data?.period === 'future') {
      if (currentRole === 'PL') {
        return QUARTERLY_DETAILS.PROJECTED_FORECAST;
      } else if (currentRole === 'MSL') {
        return QUARTERLY_DETAILS.PL_PROJECTED_FORECAST_MSL;
      } else {
        return QUARTERLY_DETAILS.PL_PROJECTED_FORECAST_GCSP;
      }
    }
  };

  const getVarianceValue = (data, currentMonth) => {
    if (data?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE) {
      return QUARTERLY_DETAILS.NOT_APPLICABLE;
    } else if (currentRole === ROLE_CONST?.PL) {
      return formatVarianceWithKAndM(data?.sold - data?.commit) || '0 K';
    } else if (currentRole === ROLE_CONST?.MSL || currentRole === ROLE_CONST?.GCSP) {
      // For MSL and GCSP, use the same logic as getCommitValueForCalculations to include audit risk
      const commitValueWithAuditRisk = getCommitValueForCalculations(data, currentMonth);
      return formatVarianceWithKAndM(data?.sold - commitValueWithAuditRisk) || '0 K';
    } else {
      return formatVarianceWithKAndM(data?.sold - data?.commit) || '0 K';
    }
  };

  const getCommitValueForCalculations = (data, currentMonth) => {
    // For MSL and GCSP roles, always use pl_commit as base value for current and future months
    // For past months, use commit value (historical data)
    // Prioritize actual_period over period for determining which value to use
    let commitValue;

    if (
      (currentRole === ROLE_CONST?.MSL || currentRole === ROLE_CONST?.GCSP) &&
      (data?.actual_period === 'current' ||
        data?.actual_period === 'future' ||
        data?.period === 'future')
    ) {
      commitValue = data?.pl_commit ?? 0;
    } else {
      commitValue = data?.period === 'past' ? data?.commit : data?.pl_commit;
    }

    // Add audit risk for MSL and GCSP roles for all periods (current, future, and past) when month matches
    if (
      (currentRole === ROLE_CONST?.MSL || currentRole === ROLE_CONST?.GCSP) &&
      currentMonth &&
      (data?.actual_period === 'current' || data?.period === 'future' || data?.period === 'past')
    ) {
      if (auditRiskData && Array.isArray(auditRiskData)) {
        const matchingAuditRisk = auditRiskData.find(
          auditItem => auditItem?.month?.toLowerCase() === currentMonth?.toLowerCase(),
        );
        if (matchingAuditRisk) {
          // Ensure both values are numbers to avoid string concatenation
          const baseCommit = Number(commitValue) || 0;
          const auditRisk = Number(matchingAuditRisk?.value) || 0;
          commitValue = baseCommit + auditRisk;
        }
      }
    }

    return commitValue;
  };

  const getForecastValue = (data, currentMonth) => {
    if (currentRole === ROLE_CONST?.PL) {
      return getValue(data?.tag, data?.commit);
    } else {
      const commitValue = getCommitValueForCalculations(data, currentMonth);
      return getValue(data?.tag, commitValue);
    }
  };

  const getForecastBtn = () => {
    if (currentRole === ROLE_CONST?.PL) {
      return buttonStyleForecastPL;
    } else if (currentRole === ROLE_CONST?.GCSP) {
      return buttonStyleForecastGCSP;
    } else {
      return buttonStyleForecastMSL;
    }
  };

  return (
    <>
      {!!monthDataInfo && !!quarterDataInfo ? (
        <CommonMonthlyBar>
          {monthDataInfo.map((item, index) => {
            return item?.calculation?.period !== 'future' &&
              item?.calculation?.actual_period !== 'current' ? (
              <CommonMonthlyContainer key={index}>
                <MonthBtnStyle>
                  <PillContainer>
                    <Month>{uppercaseFirstLetter(item.month).slice(0, 3)}</Month>
                  </PillContainer>
                  {isMobile ? (
                    <>
                      {item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={mobileBtnStyle}
                          onClick={() => {
                            navigate('/forecast');
                          }}
                        >
                          {BUTTON_TITLE.FORECAST}
                        </MotifButton>
                      ) : item.month.slice(0, 3).toLowerCase() ===
                          quarterInfo.month.toLowerCase() &&
                        item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={
                            item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                              ? disableViewButtonStyle
                              : mobileBtnStyle
                          }
                          variant="secondary"
                          onClick={() => {
                            navigate('/forecast/past', {
                              state: {
                                year: quarterData.yearText,
                                quarter: quarterData.quarterText,
                                month: item.month,
                                email: email,
                                account: account,
                                forecast_type: 'month',
                              },
                            });
                          }}
                          aria-label={`View details of month ${item.month}`}
                          disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                        >
                          <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                        </MotifButton>
                      ) : item?.calculation?.period === 'past' &&
                        item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() &&
                        item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={{
                            ...(item?.calculation?.tag === MONTH_QUARTER_STATUS?.NOT_APPLICABLE
                              ? mobileBtnStyle
                              : mobileBtnStyle),
                            ...(currentRole === ROLE_CONST?.MSL && { marginTop: 36 }),
                          }}
                          variant="secondary"
                          onClick={() => {
                            navigate('/forecast/past', {
                              state: {
                                year: quarterData.yearText,
                                quarter: quarterData.quarterText,
                                month: item.month,
                                email: email,
                                account: account,
                                forecast_type: 'month',
                              },
                            });
                          }}
                          aria-label={`View details of month ${item.month}`}
                          disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                        >
                          <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                        </MotifButton>
                      ) : item?.calculation?.period === 'future' &&
                        item.month.slice(0, 3).toLowerCase() !==
                          quarterInfo.month.toLowerCase() ? null : null}
                    </>
                  ) : null}
                </MonthBtnStyle>
                <MonthlyBarStyle>
                  <Item>
                    <Label>{QUARTERLY_DETAILS.FORECAST}</Label>
                    <Value>
                      {getForecastValue(item?.calculation, item?.month) || '0 K'}{' '}
                      {item?.calculation?.tag === 'missed' &&
                        item?.calculation?.actual_period !== 'current' && (
                          <MissedText>(Missed)</MissedText>
                        )}
                    </Value>
                  </Item>
                  <Item>
                    <Label>
                      <PillContainer style={{ alignItems: 'center' }}>
                        {QUARTERLY_DETAILS.WON}
                      </PillContainer>
                    </Label>
                    <Value>
                      {item?.calculation?.period !== 'future' ? (
                        <span>
                          {getValue(item?.calculation?.tag, item?.calculation?.sold) || '0 K'}
                        </span>
                      ) : null}
                    </Value>
                  </Item>
                  <Item>
                    <Label>{QUARTERLY_DETAILS.VARIANCE}</Label>
                    <Value>{getVarianceValue(item?.calculation, item?.month) || '0 K'}</Value>
                  </Item>

                  <Item>
                    <Label>{QUARTERLY_DETAILS.ACCURACY}</Label>
                    <Value>
                      {item?.calculation?.period !== 'future' ? (
                        <span>
                          {item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                            ? QUARTERLY_DETAILS.NOT_APPLICABLE
                            : `${getAccuracy(item?.calculation?.sold, getCommitValueForCalculations(item?.calculation, item?.month), item?.calculation?.tag) || 0}%`}
                        </span>
                      ) : null}
                    </Value>
                  </Item>
                </MonthlyBarStyle>
                {!isMobile ? (
                  <CommonColumnStyle>
                    {item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                    item?.calculation?.period !== 'future' &&
                    item?.calculation?.actual_period !== 'past' ? (
                      <MotifButton
                        style={buttonStyleForecast}
                        onClick={() => {
                          navigate('/forecast');
                        }}
                      >
                        {BUTTON_TITLE.FORECAST}
                      </MotifButton>
                    ) : item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' ? (
                      <MotifButton
                        style={{
                          ...(item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                            ? disableViewButtonStyle
                            : buttonStyle),
                          ...(currentRole === ROLE_CONST?.MSL && { marginTop: 36 }),
                        }}
                        variant="secondary"
                        onClick={() => {
                          navigate('/forecast/past', {
                            state: {
                              year: quarterData.yearText,
                              quarter: quarterData.quarterText,
                              month: item.month,
                              email: email,
                              account: account,
                              forecast_type: 'month',
                            },
                          });
                        }}
                        aria-label={`View details of month ${item.month}`}
                        disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                      >
                        <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                      </MotifButton>
                    ) : item?.calculation?.period === 'past' &&
                      item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' ? (
                      <MotifButton
                        style={{
                          ...(item?.calculation?.tag === MONTH_QUARTER_STATUS?.NOT_APPLICABLE
                            ? disableViewButtonStyle
                            : buttonStyle),
                          ...(currentRole === ROLE_CONST?.MSL && { marginTop: 36 }),
                        }}
                        variant="secondary"
                        onClick={() => {
                          navigate('/forecast/past', {
                            state: {
                              year: quarterData.yearText,
                              quarter: quarterData.quarterText,
                              month: item.month,
                              email: email,
                              account: account,
                              forecast_type: 'month',
                            },
                          });
                        }}
                        aria-label={`View details of month ${item.month}`}
                        disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                      >
                        <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                      </MotifButton>
                    ) : item?.calculation?.period === 'future' &&
                      item.month.slice(0, 3).toLowerCase() !==
                        quarterInfo.month.toLowerCase() ? null : null}
                  </CommonColumnStyle>
                ) : null}

                {!!monthDataInfo && monthDataInfo.length - 2 >= index ? <CommonSeparator /> : null}
              </CommonMonthlyContainer>
            ) : (
              <CommonMonthlyContainer key={index}>
                <MonthBtnStyle>
                  <PillContainer>
                    <Month>{uppercaseFirstLetter(item.month).slice(0, 3)}</Month>
                    {!!quarterInfo &&
                    item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                    item?.calculation?.period !== 'future' ? (
                      <CurrentPill />
                    ) : null}
                    {item?.calculation?.period === 'future' ? <FuturePillDashboard /> : null}
                  </PillContainer>
                  {isMobile ? (
                    <>
                      {item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={mobileBtnStyle}
                          onClick={() => {
                            navigate('/forecast');
                          }}
                        >
                          {BUTTON_TITLE.FORECAST}
                        </MotifButton>
                      ) : item.month.slice(0, 3).toLowerCase() ===
                          quarterInfo.month.toLowerCase() &&
                        item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={
                            item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                              ? disableViewButtonStyle
                              : mobileBtnStyle
                          }
                          variant="secondary"
                          onClick={() => {
                            navigate('/forecast/past', {
                              state: {
                                year: quarterData.yearText,
                                quarter: quarterData.quarterText,
                                month: item.month,
                                email: email,
                                account: account,
                                forecast_type: 'month',
                              },
                            });
                          }}
                          aria-label={`View details of month ${item.month}`}
                          disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                        >
                          <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                        </MotifButton>
                      ) : item?.calculation?.period === 'past' &&
                        item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() &&
                        item?.calculation?.period !== 'future' ? (
                        <MotifButton
                          style={
                            item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                              ? disableViewButtonStyle
                              : mobileBtnStyle
                          }
                          variant="secondary"
                          onClick={() => {
                            navigate('/forecast/past', {
                              state: {
                                year: quarterData.yearText,
                                quarter: quarterData.quarterText,
                                month: item.month,
                                email: email,
                                account: account,
                                forecast_type: 'month',
                              },
                            });
                          }}
                          aria-label={`View details of month ${item.month}`}
                          disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                        >
                          <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                        </MotifButton>
                      ) : item?.calculation?.period === 'future' &&
                        item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() ? (
                        <InvisibleDiv2 />
                      ) : null}
                    </>
                  ) : null}
                </MonthBtnStyle>
                <MonthlyBarStyle>
                  {/*to accomodate the (amount)K/M/B label in line with the title label */}
                  {currentRole === 'MSL' && item?.calculation?.period === 'future' ? (
                    <ItemMSLFuture>
                      <LabelMSLFuture>{forecastLabel(item?.calculation)}</LabelMSLFuture>
                      <ValueMSLFuture>
                        {getForecastValue(item?.calculation, item?.month) || '0 K'}
                      </ValueMSLFuture>
                    </ItemMSLFuture>
                  ) : (
                    <Item>
                      <Label>{forecastLabel(item?.calculation)}</Label>
                      <Value>{getForecastValue(item?.calculation, item?.month) || '0 K'}</Value>
                    </Item>
                  )}

                  {currentRole?.toLowerCase() === ROLE_CONST.MSL?.toLowerCase() ? (
                    <Item>
                      <Label>{QUARTERLY_DETAILS.GCSP_JUDGE}</Label>
                      <Value>
                        {item?.calculation?.gcsp_judge === null ? (
                          <span>{QUARTERLY_DETAILS.TBD}</span>
                        ) : (
                          GcspformatNumberWithKAndM(item?.calculation?.gcsp_judge) || '0 K'
                        )}
                      </Value>
                    </Item>
                  ) : null}
                  {currentRole?.toLowerCase() !== ROLE_CONST.PL?.toLowerCase() ? (
                    <>
                      <Item>
                        <Label>{QUARTERLY_DETAILS.YOUR_JUDGE}</Label>
                        <Value>
                          {item?.calculation?.judge === null ? (
                            <span>{QUARTERLY_DETAILS.TBD}</span>
                          ) : (
                            GcspformatNumberWithKAndM(item?.calculation?.judge) || '0 K'
                          )}
                        </Value>
                      </Item>
                      <Item>
                        <PillContainer style={{ alignItems: 'center' }}>
                          <Label>
                            {item?.calculation?.actual_period === 'current'
                              ? QUARTERLY_DETAILS.TOTAL_FORECAST
                              : item?.calculation?.period === 'future'
                                ? QUARTERLY_DETAILS.TOTAL_PROJECTED_FORECAST
                                : QUARTERLY_DETAILS.YOUR_PROJECTED}
                          </Label>
                        </PillContainer>
                        <Value>
                          {item?.calculation?.actual_period === 'current' ||
                          item?.calculation?.period === 'future'
                            ? totalForecastValue(item?.calculation, item?.month)
                            : getValue(item?.calculation?.tag, item?.calculation?.commit) || '0 K'}
                        </Value>
                      </Item>
                    </>
                  ) : (
                    <Item>
                      <Label>{QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}</Label>
                      <Value>
                        {item?.calculation?.judge === null ? (
                          <span>{QUARTERLY_DETAILS.TBD}</span>
                        ) : (
                          GcspformatNumberWithKAndM(item?.calculation?.noCommit) || '0 K'
                        )}
                      </Value>
                    </Item>
                  )}
                </MonthlyBarStyle>
                {!isMobile ? (
                  <CommonColumnStyle>
                    {item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                    item?.calculation?.period !== 'future' &&
                    new Date(
                      TodaysDate.getFullYear(),
                      TodaysDate.getMonth(),
                      TodaysDate.getDate(),
                    ) <= new Date(quarterInfo.persona_closedate) ? (
                      <MotifButton
                        style={getForecastBtn()}
                        onClick={() => {
                          navigate('/forecast');
                        }}
                      >
                        {BUTTON_TITLE.FORECAST}
                      </MotifButton>
                    ) : item.month.slice(0, 3).toLowerCase() === quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' &&
                      TodaysDate > new Date(quarterInfo.persona_closedate) ? (
                      <MotifButton
                        style={
                          item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                            ? disableViewButtonStyle
                            : buttonStyle
                        }
                        variant="secondary"
                        onClick={() => {
                          navigate('/forecast/past', {
                            state: {
                              year: quarterData.yearText,
                              quarter: quarterData.quarterText,
                              month: item.month,
                              email: email,
                              account: account,
                              forecast_type: 'month',
                            },
                          });
                        }}
                        aria-label={`View details of month ${item.month}`}
                        disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                      >
                        <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                      </MotifButton>
                    ) : item?.calculation?.period === 'past' &&
                      item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() &&
                      item?.calculation?.period !== 'future' ? (
                      <MotifButton
                        style={
                          item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE
                            ? disableViewButtonStyle
                            : buttonStyle
                        }
                        variant="secondary"
                        onClick={() => {
                          navigate('/forecast/past', {
                            state: {
                              year: quarterData.yearText,
                              quarter: quarterData.quarterText,
                              month: item.month,
                              email: email,
                              account: account,
                              forecast_type: 'month',
                            },
                          });
                        }}
                        aria-label={`View details of month ${item.month}`}
                        disabled={item?.calculation?.tag === MONTH_QUARTER_STATUS.NOT_APPLICABLE}
                      >
                        <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                      </MotifButton>
                    ) : item?.calculation?.period === 'future' &&
                      item.month.slice(0, 3).toLowerCase() !== quarterInfo.month.toLowerCase() ? (
                      <InvisibleDiv2 />
                    ) : null}
                  </CommonColumnStyle>
                ) : null}

                {!!monthDataInfo && monthDataInfo.length - 2 >= index ? <CommonSeparator /> : null}
              </CommonMonthlyContainer>
            );
          })}
        </CommonMonthlyBar>
      ) : null}
    </>
  );
};

export default MonthlyDetails;
